import { blue } from '@ant-design/colors'
import * as EvaluationParser from '@cck/backend/dist/evaluation/ExcelParser'
import { BaseFile, FileType } from '@cck/common/dist/data/BaseFile'
import * as Evaluation from '@cck/common/dist/data/Evaluation'
import { Staff } from '@cck/common/dist/data/Staff'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from 'antd/lib/typography'
import clsx from 'clsx'
import _ from 'lodash'
import React from 'react'
import { useDropzone } from 'react-dropzone'

import { dateCellRenderer } from '../../../base/utils/FileUtils'
import AlertMessage, { AlertMessageHandler } from '../../common/AlertMessage'
import CommonAgGrid from '../../common/CommonAgGrid'
import NoticeTextView from '../../common/NoticeTextView'
import { StaffCellRenderer } from '../../common/StaffRenderer'
import UploadIconAndText from '../common/UploadIconAndText'
import { getFileUploaderColumns } from '../view/EvaluationFileUploader'

const useStyles = makeStyles((theme: Theme) => ({
  dropzone: {
    display: 'flex',
    position: 'relative',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    background: 'WhiteSmoke',
    border: '1px dashed lightgray',
    marginBottom: theme.spacing(1)
  },
  dragActive: {
    borderColor: blue.primary
  }
}))

interface Props {
  evaluationName: string
  controlId: string
  staffs: Staff[]
  evidenceFiles?: BaseFile[]
  setEvidences: (files: BaseFile[]) => void
}

const EvidenceFileUploader: React.FC<Props> = ({
  evaluationName,
  controlId,
  staffs,
  evidenceFiles,
  setEvidences
}) => {
  const classes = useStyles()
  const alertRef = React.useRef<AlertMessageHandler>(null)

  const onDrop = React.useCallback(
    (originalFiles) => {
      const acceptedFiles: File[] = []
      _.forEach(originalFiles, (file) => {
        acceptedFiles.push(new File([file], file.name.normalize('NFC'), { type: file.type }))
      })
      const fileInfos = EvaluationParser.parseFileInfo(
        Evaluation.Type.Operation,
        acceptedFiles,
        evaluationName,
        controlId
      )
      if (_.every(fileInfos, (fileInfo) => fileInfo.type !== FileType.Evidence)) {
        alertRef.current?.showAlert('error', '증빙 파일을 업로드해주세요.')
        return
      }

      const newEvidenceFiles: BaseFile[] = _.cloneDeep(evidenceFiles) || []
      _.forEach(fileInfos, (fileInfo) => {
        if (fileInfo.type === FileType.Evidence) {
          const matched = _.findIndex(newEvidenceFiles, { name: fileInfo.name })
          if (matched >= 0) {
            _.pullAt(newEvidenceFiles, [matched])
          }
          newEvidenceFiles.push(fileInfo)
        }
      })
      setEvidences(newEvidenceFiles)
    },
    [evidenceFiles, evaluationName, controlId]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: !_.isEmpty(evidenceFiles)
  })

  const deleteFile = (allFiles: BaseFile[], file: BaseFile): void => {
    const newFiles = allFiles.filter((item) => item.name !== file.name)
    setEvidences(newFiles)
  }

  return (
    <div>
      <Typography.Title level={5}>증빙 파일 업로드</Typography.Title>
      <div
        className={clsx(classes.dropzone, { [classes.dragActive]: isDragActive })}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {_.isEmpty(evidenceFiles) && <UploadIconAndText isDragActive={isDragActive} />}
        {!_.isEmpty(evidenceFiles) && (
          <CommonAgGrid
            autoSize="sizeColumnsToFit"
            noBorder
            columnDefs={getFileUploaderColumns(Evaluation.Type.Operation, staffs, deleteFile)}
            domLayout="autoHeight"
            frameworkComponents={{ dateCellRenderer, StaffCellRenderer }}
            rowData={evidenceFiles}
          />
        )}
      </div>
      <NoticeTextView>
        증빙 파일명은{' '}
        <Typography.Text type="danger" strong>
          ({evaluationName})({controlId})(증빙)파일이름.확장자
        </Typography.Text>
        으로 업로드해야합니다.
      </NoticeTextView>
      <AlertMessage ref={alertRef} />
    </div>
  )
}

export default EvidenceFileUploader
